import { useState, useEffect } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Share2, FileSignature, CheckCircle2, AlertTriangle, XCircle, CloudSun, Globe, Luggage, Bus, ShieldCheck } from "lucide-react";
import { RD_TINT, RD_LABEL, FIX_ROUTE, FIX_LABEL } from "./constants";

const PACKING_PRESETS = {
  Beach: ["Sunscreen SPF 50+", "Swimwear", "Beach towel", "Sunglasses", "Flip-flops", "Hat / cap"],
  Business: ["Laptop & charger", "Formal outfits", "Business cards", "Notebook & pen", "Travel adapter", "Presentation materials"],
  Winter: ["Heavy jacket", "Thermal wear", "Gloves & beanie", "Moisturiser & lip balm", "Warm socks", "Waterproof boots"],
};

const InsuranceBlock = ({ dest }) => {
  const [added, setAdded] = useState(false);
  const [busy, setBusy] = useState(false);
  const providers = [["ACKO Travel", "https://www.acko.com/travel-insurance/"], ["Tata AIG", "https://www.tataaig.com/travel-insurance"], ["Policybazaar", "https://www.policybazaar.com/travel-insurance/"]];
  const addReminder = async () => {
    setBusy(true);
    try { await api.post("/checklist/item", { title: `Buy travel insurance for ${dest || "your trip"}` }); setAdded(true); toast.success("Added to your checklist"); }
    catch { toast.error("Could not add reminder"); } finally { setBusy(false); }
  };
  return (
    <div className="mt-3 rounded-lg border border-slate-100 p-2.5" data-testid="tcc-insurance">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-600"><ShieldCheck className="h-3.5 w-3.5" /> Travel insurance</p>
      <p className="mt-0.5 text-[11px] text-slate-400">Compare & buy from trusted providers (opens their official site — any prices shown are theirs, never ours).</p>
      <div className="mt-1.5 flex flex-wrap gap-1.5">
        {providers.map(([n, u]) => <a key={n} href={u} target="_blank" rel="noreferrer noopener noreferrer" data-testid={`insurance-link-${n.split(" ")[0].toLowerCase()}`} className="rounded-full border border-slate-200 px-2.5 py-0.5 text-[11px] font-medium text-indigo-600 hover:border-indigo-300">{n} ↗</a>)}
      </div>
      <button onClick={addReminder} disabled={busy || added} data-testid="insurance-add-reminder" className="mt-2 rounded-full bg-indigo-500 px-3 py-1 text-[11px] font-medium text-white disabled:opacity-50">{added ? "Added ✓" : "Remind me to buy insurance"}</button>
    </div>
  );
};

const PackingEditor = ({ items, tripId, weather, packingCustom }) => {
  const [list, setList] = useState(() => (items || []).map((p) => typeof p === "string" ? { text: p, done: false } : { text: p.text, done: !!p.done }));
  const [txt, setTxt] = useState("");
  const [saving, setSaving] = useState(false);
  const suggested = (() => {
    const days = weather?.days || [];
    if (!days.length) return null;
    const avgHi = days.reduce((s, d) => s + (d.hi ?? 0), 0) / days.length;
    if (avgHi >= 28) return "Beach";
    if (avgHi <= 12) return "Winter";
    return null;
  })();
  const persist = async (next) => {
    setSaving(true);
    try { await api.put(`/trips/${tripId}/packing`, { items: next }); } catch { /* keep local */ } finally { setSaving(false); }
  };
  const toggle = (i) => { const next = list.map((p, j) => j === i ? { ...p, done: !p.done } : p); setList(next); persist(next); };
  const remove = (i) => { const next = list.filter((_, j) => j !== i); setList(next); persist(next); };
  const add = () => { const t = txt.trim(); if (!t) return; const next = [...list, { text: t, done: false }]; setList(next); setTxt(""); persist(next); };
  const applyPreset = (name) => {
    const have = new Set(list.map((p) => p.text.toLowerCase()));
    const additions = PACKING_PRESETS[name].filter((t) => !have.has(t.toLowerCase())).map((t) => ({ text: t, done: false }));
    if (!additions.length) return;
    const next = [...list, ...additions]; setList(next); persist(next);
  };
  useEffect(() => {
    if (!packingCustom && suggested) applyPreset(suggested);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return (
    <div className="mt-3 rounded-lg border border-slate-100 p-2.5" data-testid="tcc-packing">
      <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-600"><Luggage className="h-3.5 w-3.5" /> Packing checklist{saving ? " · saving…" : ""}</p>
      <div className="mt-1.5 flex flex-wrap gap-1.5" data-testid="packing-presets">
        {Object.keys(PACKING_PRESETS).map((name) => (
          <button key={name} onClick={() => applyPreset(name)} data-testid={`packing-preset-${name.toLowerCase()}`}
            className={`rounded-full border px-2.5 py-0.5 text-[11px] font-medium ${suggested === name ? "border-indigo-400 bg-indigo-50 text-indigo-600" : "border-slate-200 text-slate-500 hover:border-indigo-300 hover:text-indigo-600"}`}>+ {name}{suggested === name ? " ★" : ""}</button>
        ))}
      </div>
      {suggested && <p className="mt-1 text-[11px] text-indigo-500" data-testid="packing-suggest">Suggested for this trip's weather: {suggested}</p>}
      <ul className="mt-2 space-y-1">
        {list.map((p, i) => (
          <li key={i} className="flex items-center gap-2 text-xs text-slate-600" data-testid={`packing-item-${i}`}>
            <input type="checkbox" checked={p.done} onChange={() => toggle(i)} data-testid={`packing-toggle-${i}`} className="h-3.5 w-3.5 accent-indigo-500" />
            <span className={p.done ? "text-slate-400 line-through" : ""}>{p.text}</span>
            <button onClick={() => remove(i)} data-testid={`packing-del-${i}`} className="ml-auto text-sm text-slate-300 hover:text-rose-500">×</button>
          </li>
        ))}
      </ul>
      <div className="mt-2 flex gap-2">
        <input value={txt} onChange={(e) => setTxt(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()} placeholder="Add an item…" data-testid="packing-add-input"
          className="flex-1 rounded-lg border border-slate-200 px-2 py-1 text-xs outline-none focus:border-indigo-300" />
        <button onClick={add} data-testid="packing-add-btn" className="rounded-lg bg-indigo-500 px-3 py-1 text-xs font-medium text-white">Add</button>
      </div>
    </div>
  );
};

export const ReadinessDialog = ({ rd, onClose, rdPrefs, onTogglePref, onItinerary, onShare, showPreview, setShowPreview, onFix }) => (
  <Dialog open={!!rd} onOpenChange={(o) => !o && onClose()}>
    <DialogContent className="max-h-[88vh] overflow-y-auto" data-testid="readiness-dialog">
      <DialogHeader><DialogTitle>Trip Control Center</DialogTitle></DialogHeader>
      {rd && (
        <div>
          <div className="mb-3 flex justify-end gap-2">
            <button onClick={onItinerary} data-testid="readiness-itinerary" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 hover:border-emerald-300"><FileSignature className="h-3.5 w-3.5" /> Itinerary / Print</button>
            <button onClick={onShare} data-testid="readiness-share" className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 px-3 py-1 text-xs font-medium text-slate-600 hover:border-indigo-300"><Share2 className="h-3.5 w-3.5" /> Share</button>
          </div>
          <div className={`flex items-center gap-3 rounded-xl p-3 ${RD_TINT[rd.status]}`} data-testid={`readiness-status-${rd.status}`}>
            <span className="text-2xl font-bold">{rd.score.ok}/{rd.score.total}</span>
            <div>
              <p className="font-semibold">{RD_LABEL[rd.status]}</p>
              <p className="text-xs opacity-80">{rd.trip?.destination || rd.trip?.title}</p>
            </div>
          </div>
          {typeof rd.countdown?.days === "number" && (
            <p className="mt-2 text-xs font-medium text-slate-500" data-testid="readiness-countdown">
              {rd.countdown.days > 0 ? `${rd.countdown.days} day${rd.countdown.days !== 1 ? "s" : ""} to departure` : rd.countdown.days === 0 ? "Departs today 🎉" : "Trip in progress or past"}
            </p>
          )}
          <div className="mt-3 space-y-2">
            {rd.checks.map((c) => (
              <div key={c.key} className="flex items-start gap-2">
                {c.status === "ok" ? <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" /> : c.status === "warn" ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-500" /> : <XCircle className="mt-0.5 h-4 w-4 shrink-0 text-rose-500" />}
                <div className="flex-1"><p className="text-sm font-medium text-slate-700">{c.label}</p><p className="text-xs text-slate-400">{c.detail}</p></div>
                {c.status !== "ok" && FIX_ROUTE[c.key] && (
                  <button onClick={() => onFix(c.key)} data-testid={`readiness-fix-${c.key}`}
                    className="shrink-0 rounded-full bg-indigo-50 px-2.5 py-1 text-[11px] font-semibold text-indigo-600 hover:bg-indigo-100">{FIX_LABEL[c.key] || "Fix"}</button>
                )}
              </div>
            ))}
          </div>
          <div className="mt-3 rounded-lg border border-slate-100 p-2.5" data-testid="readiness-alert-prefs">
            <p className="text-xs font-semibold text-slate-600">Email me before departure at</p>
            <div className="mt-1.5 flex gap-2">
              {[30, 7, 1].map((m) => (
                <button key={m} onClick={() => onTogglePref(m)} data-testid={`alert-pref-${m}`}
                  className={`rounded-full px-3 py-1 text-xs font-medium transition ${rdPrefs.includes(m) ? "bg-indigo-500 text-white" : "border border-slate-200 text-slate-500 hover:border-indigo-300"}`}>{m} day{m !== 1 ? "s" : ""}</button>
              ))}
            </div>
            <button onClick={() => setShowPreview((p) => !p)} data-testid="alert-preview-toggle" className="mt-2 text-[11px] font-medium text-indigo-600 hover:underline">{showPreview ? "Hide" : "Preview"} reminder email</button>
            {showPreview && (() => {
              const pending = rd.checks.filter((c) => c.status !== "ok").map((c) => c.label);
              const sorted = [...rdPrefs].sort((a, b) => b - a);
              const days = typeof rd.countdown?.days === "number" ? rd.countdown.days : null;
              const milestone = sorted.find((m) => days == null || days >= m) ?? sorted[sorted.length - 1] ?? 7;
              return (
                <div className="mt-2 rounded-lg border border-slate-100 bg-slate-50 p-2.5 text-[11px] text-slate-500" data-testid="alert-preview">
                  <p className="font-semibold text-slate-600">Subject: {pending.length > 0 ? `${milestone} day${milestone !== 1 ? "s" : ""} to ${rd.trip?.title || "your trip"} — a few things to finish` : `${rd.trip?.title || "your trip"} — you're all set`}</p>
                  {pending.length > 0
                    ? <p className="mt-1">Hi there, a few trip-readiness items still need your attention: <b>{pending.join(", ")}</b>. Open Suffle Online to finish them before you fly. Safe travels!</p>
                    : <p className="mt-1">You're all set for this trip — every readiness item is complete. We won't send a reminder unless something changes. Safe travels!</p>}
                </div>
              );
            })()}
          </div>
          <p className="mt-3 text-[11px] text-slate-400">Based only on your own saved documents, bookings and reminders.</p>
          {rd.linked?.bookings?.length > 0 && (
            <div className="mt-3 rounded-lg bg-slate-50 p-2.5" data-testid="readiness-linked">
              <p className="text-xs font-semibold text-slate-600">Linked to this trip</p>
              {rd.linked.bookings.map((b, i) => <p key={i} className="mt-0.5 text-xs text-slate-500">• {[b.title, b.date, b.time, b.location].filter(Boolean).join(" · ")}</p>)}
            </div>
          )}
          {rd.transport?.length > 0 && (
            <div className="mt-3 rounded-lg border border-slate-100 p-2.5" data-testid="tcc-transport">
              <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-600"><Bus className="h-3.5 w-3.5" /> Transport & transfers</p>
              {rd.transport.map((t, i) => <p key={i} className="mt-0.5 text-xs text-slate-500">• {[t.title, t.date, t.location].filter(Boolean).join(" · ")}</p>)}
            </div>
          )}
          <div className="mt-3 rounded-lg border border-slate-100 p-2.5" data-testid="tcc-weather">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-600"><CloudSun className="h-3.5 w-3.5" /> Weather</p>
            {rd.weather?.available ? (
              <>
                <p className="mt-0.5 text-[11px] text-slate-400">{rd.weather.place} · source {rd.weather.source}</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {rd.weather.days?.slice(0, 7).map((w, i) => (
                    <span key={i} className="rounded-lg bg-sky-50 px-2 py-1 text-[11px] text-sky-700">{String(w.date).slice(5).split("-").reverse().join("/")}: {Math.round(w.lo)}–{Math.round(w.hi)}°{w.rain != null ? ` · ${w.rain}% rain` : ""}</span>
                  ))}
                </div>
              </>
            ) : <p className="mt-0.5 text-xs text-slate-400">{rd.weather?.reason || "Weather appears here within ~16 days of travel. We never make up forecasts."}</p>}
          </div>
          <div className="mt-3 rounded-lg border border-slate-100 p-2.5" data-testid="tcc-requirements">
            <p className="flex items-center gap-1.5 text-xs font-semibold text-slate-600"><Globe className="h-3.5 w-3.5" /> Entry & visa requirements</p>
            <p className="mt-0.5 text-xs text-slate-500">{rd.requirements?.note}</p>
            {rd.requirements?.official_link && <a href={rd.requirements.official_link} target="_blank" rel="noreferrer noopener noreferrer" className="mt-1 inline-block text-[11px] font-medium text-indigo-600 hover:underline" data-testid="tcc-req-link">Check official requirements →</a>}
          </div>
          {rd.trip?.record_id && <InsuranceBlock dest={rd.trip?.destination || rd.trip?.title} />}
          {rd.trip?.record_id && <PackingEditor key={rd.trip.record_id} items={rd.packing || []} tripId={rd.trip.record_id} weather={rd.weather} packingCustom={rd.packing_custom} />}
        </div>
      )}
    </DialogContent>
  </Dialog>
);
